import {
  enabledAuthProviders,
  isUserRole,
  type AuthSession,
  type EnabledAuthProviderKind,
} from "@/modules/auth/public";
import { createLogger } from "@/logging";

const authSessionStorageKey = "hatchery-registry:auth:session";

// 토큰이 요청 중 만료되는 race를 피하기 위해 약간의 여유를 둔다.
const expiryMarginMs = 30_000;

const logger = createLogger("authSessionStorage");

export function loadPersistedAuthSession(now: number = Date.now()): AuthSession | null {
  const raw = readRawSession();
  if (raw === null) {
    return null;
  }

  const session = parseAuthSession(raw);
  if (session === null) {
    // 파싱에 실패한 저장 값은 즉시 비운다. 그러면 다음 init에서 다시 파싱하지 않는다.
    clearPersistedAuthSession();
    return null;
  }

  if (isExpired(session, now)) {
    clearPersistedAuthSession();
    return null;
  }

  return session;
}

export function persistAuthSession(session: AuthSession): void {
  try {
    window.localStorage.setItem(authSessionStorageKey, JSON.stringify(session));
  } catch (error) {
    // 저장 실패는 로그인을 막지 않는다. 새로고침하면 로그인 상태가 유지되지 않을 뿐이다.
    logger.warn("Failed to persist auth session.", error);
  }
}

export function clearPersistedAuthSession(): void {
  try {
    window.localStorage.removeItem(authSessionStorageKey);
  } catch {
    // 저장소가 막힌 환경에서는 지울 값도 없다.
  }
}

export function isAuthSessionStorageEvent(event: StorageEvent): boolean {
  return event.key === null || event.key === authSessionStorageKey;
}

function readRawSession(): string | null {
  try {
    return window.localStorage.getItem(authSessionStorageKey);
  } catch {
    return null;
  }
}

function parseAuthSession(raw: string): AuthSession | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (typeof parsed !== "object" || parsed === null) {
    return null;
  }

  const candidate = parsed as Record<string, unknown>;

  const provider = candidate.provider;
  if (
    typeof provider !== "string" ||
    !enabledAuthProviders.includes(provider as EnabledAuthProviderKind)
  ) {
    return null;
  }

  const rawRole = candidate.role;
  if (!isUserRole(rawRole)) {
    return null;
  }
  const persistedRole = rawRole;

  const uid = candidate.uid;
  const displayName = candidate.displayName;
  const accessToken = candidate.accessToken;
  if (
    typeof uid !== "string" ||
    typeof displayName !== "string" ||
    typeof accessToken !== "string"
  ) {
    return null;
  }
  if (uid === "" || accessToken === "") {
    return null;
  }

  const email = candidate.email;
  if (email !== null && typeof email !== "string") {
    return null;
  }

  const memberUuid = candidate.memberUuid;
  if (memberUuid !== undefined && memberUuid !== null && typeof memberUuid !== "string") {
    return null;
  }

  const accessTokenExpiresAt = candidate.accessTokenExpiresAt;
  if (accessTokenExpiresAt !== null && typeof accessTokenExpiresAt !== "string") {
    return null;
  }
  if (typeof accessTokenExpiresAt === "string" && Number.isNaN(Date.parse(accessTokenExpiresAt))) {
    return null;
  }

  return {
    provider: provider as EnabledAuthProviderKind,
    uid,
    memberUuid: memberUuid ?? null,
    displayName,
    email,
    role: persistedRole,
    accessToken,
    accessTokenExpiresAt,
  };
}

function isExpired(session: AuthSession, now: number): boolean {
  if (session.accessTokenExpiresAt === null) {
    return false;
  }
  const expiresAtMs = Date.parse(session.accessTokenExpiresAt);
  if (Number.isNaN(expiresAtMs)) {
    return true;
  }
  return expiresAtMs - expiryMarginMs <= now;
}
